import React from 'react';
import Select from 'react-select';

import { T as CT, money as customerMoney } from '../customer/customers.theme';
import { Dialog, DialogHeader, ErrorLine, PrimaryButton } from '../customer/CustomersUI';

/**
 * Moving an order off cash and onto a customer's account.
 *
 * The order was rung in as cash — somebody has eaten and is not paying now.
 * This is the till's way of saying so: pick whose book it goes on, and the
 * server opens the transaction, raises their balance and points the order at
 * them.
 *
 * Nothing about the order's CONTENTS changes here, and that is the important
 * part: the request carries no `items`, so the server's diff finds nothing
 * added and no station prints a thing. Settling a bill must never send food
 * to a kitchen.
 *
 * Reversing it is the same route with `paymentType: 'cash'` — the server
 * voids the transaction and gives the customer their balance back — but that
 * is not offered here yet.
 */

const COPY = {
  title: (n) => `Bill order #${n}`,
  pick: 'Dooro Macaamiilka',
  loading: 'Loading customers…',
  empty: 'No customers yet',
  owes: 'currently owes',
  after: 'After this',
  required: 'Choose the customer to bill',
  confirm: 'Bill to account',
  failed: 'Could not bill this order. Try again.',
  note:
    'The order moves onto this customer account. Nothing is sent to the kitchen and the items do not change.',
};

export default function BillToCustomerDialog({
  entry,
  customers,
  loading,
  currency,
  onClose,
  onConfirm,
}) {
  const [customerId, setCustomerId] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  const selected = customers.find((row) => row._id === customerId) || null;

  const options = React.useMemo(
    () =>
      customers.map((customer) => ({
        value: customer._id,
        label: customer.phone ? `${customer.name} · ${customer.phone}` : customer.name,
      })),
    [customers]
  );

  const submit = async () => {
    if (busy) return;
    if (!customerId) return setError(COPY.required);

    setBusy(true);
    setError('');

    try {
      await onConfirm(entry, customerId);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || COPY.failed);
      setBusy(false);
    }
  };

  return (
    <Dialog onClose={onClose} locked={busy} width={430}>
      <div style={{ padding: '22px 24px 24px' }}>
        <DialogHeader
          title={COPY.title(entry.orderNumber)}
          onClose={onClose}
          locked={busy}
        />

        <div style={{ paddingTop: 18 }}>
          <Select
            options={options}
            value={options.find((option) => option.value === customerId) || null}
            onChange={(option) => {
              setCustomerId(option ? option.value : '');
              setError('');
            }}
            isDisabled={busy}
            isClearable
            autoFocus
            placeholder={loading ? COPY.loading : COPY.pick}
            noOptionsMessage={() => COPY.empty}
            styles={{
              control: (base) => ({
                ...base,
                minHeight: 46,
                borderRadius: CT.radiusPill,
                borderColor: CT.borderStrong,
                boxShadow: 'none',
                paddingLeft: 6,
              }),
              menu: (base) => ({ ...base, zIndex: 5 }),
            }}
          />
        </div>

        {/* What this does to their book, before it is done. The balance is the
            figure a cashier is asked about at the counter. */}
        {selected ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              marginTop: 14,
              padding: '12px 14px',
              background: CT.sunk,
              border: `1px solid ${CT.border}`,
              borderRadius: CT.radiusControl,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
              <span style={{ fontSize: 12.5, color: CT.inkSoft }}>
                {selected.name} {COPY.owes}
              </span>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: CT.ink }}>
                {customerMoney(selected.balance, currency)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
              <span style={{ fontSize: 12.5, color: CT.inkSoft }}>{COPY.after}</span>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: CT.brandInk }}>
                {customerMoney(Number(selected.balance || 0) + Number(entry.total || 0), currency)}
              </span>
            </div>
          </div>
        ) : null}

        <div style={{ fontSize: 11.5, color: CT.inkFaint, lineHeight: 1.6, paddingTop: 12 }}>
          {COPY.note}
        </div>

        <div style={{ paddingTop: 14 }}>
          <ErrorLine>{error}</ErrorLine>
        </div>

        <PrimaryButton
          onClick={submit}
          busy={busy}
          style={{ width: '100%', marginTop: error ? 12 : 18 }}
        >
          {COPY.confirm}
        </PrimaryButton>
      </div>
    </Dialog>
  );
}
